import { ImagePlus, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import api from "../../../configs/api";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
import { ModalWrapper } from "../common/ModalWrapper";
import toast from "react-hot-toast";

const EditCarrierModal = ({ modal, onCarrierupdated }) => {
  const { isOpen, mode, data: carrierId, close } = modal;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    date: "",
    time: "",
    location: "",
    experience: "",
    salary: "",
    sequence: 0,
  });

  useEffect(() => {
    if (!isOpen) return;

    const fetchCarrier = async () => {
      if (mode !== "edit" || !carrierId) return;

      try {
        setLoading(true);

        const res = await api.get(`/carriers/${carrierId}`);
        const carrier = res.data?.data;

        setForm({
          name: carrier.name || "",
          description: carrier.description || "",
          date: carrier.date || "",
          time: carrier.time || "",
          location: carrier.location || "",
          experience: carrier.experience || "",
          salary: carrier.salary || "",
          sequence: carrier.sequence || 0,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (mode === "create") {
      setForm({
        name: "",
        description: "",
        date: "",
        time: "",
        location: "",
        experience: "",
        salary: "",
        sequence: 0,
      });
    }

    fetchCarrier();
  }, [isOpen, mode, carrierId]);

  /*  FIELD UPDATE  */
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /*  SUBMIT  */
  const handleSubmit = async () => {
    try {
      setSaving(true);
      let res;

      if (mode === "create") {
        res = await api.post("/carriers", form);
        toast.success("Carrier created successfully");
      } else {
        res = await api.patch(`/carriers/${carrierId}`, form);
        toast.success("Carrier updated successfully");
      }

      const carrier = res.data?.data;

      if (mode === "edit" && onCarrierupdated) {
        onCarrierupdated(carrier);
      }

      close();
    } catch (err) {
      toast.error("Carrier action failed");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /*  UI  */

  return (
    <ModalWrapper
      title={mode === "create" ? "Create Carrier" : "Edit Carrier"}
      isOpen={isOpen}
      onClose={close}
      primaryActionLabel={mode === "create" ? "Create Carrier" : "Save Changes"}
      primaryLoading={saving}
      allowEasyClose={false}
      onPrimaryAction={handleSubmit}
      maxWidth="max-w-3xl"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-5 text-sm">
          {/* NAME */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Carrier Name
            </label>
            <input
              value={form.name}
              placeholder="e.g. Tata Memorial Center"
              onChange={(e) => updateField("name", e.target.value)}
              className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Description
            </label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              className="mt-1 w-full px-4 py-3 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* DATE & TIME */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-headingText">
                Date
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => updateField("date", e.target.value)}
                className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-headingText">
                Time
              </label>
              <input
                type="text"
                placeholder="eg., Full Time"
                value={form.time}
                onChange={(e) => updateField("time", e.target.value)}
                className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
              />
            </div>
          </div>

          {/* LOCATION */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Location
            </label>
            <input
              value={form.location}
              placeholder="e.g. Mumbai"
              onChange={(e) => updateField("location", e.target.value)}
              className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* EXPERIENCE */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Experience
            </label>
            <input
              value={form.experience}
              placeholder="e.g. 2+ years"
              onChange={(e) => updateField("experience", e.target.value)}
              className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* SALARY */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Salary
            </label>
            <input
              type="text"
              value={form.salary}
              placeholder="e.g. 5-6 LPA"
              onChange={(e) => updateField("salary", e.target.value)}
              className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* SEQUENCE */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Sequence
            </label>
            <input
              type="number"
              value={form.sequence}
              onChange={(e) => updateField("sequence", e.target.value)}
              className="mt-1 h-[44px] w-full px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default EditCarrierModal;
